import { useMemo, useState } from "react";
import { ChevronDown, Clock, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TIMEZONE_AREAS } from "@/data/timezoneConverter";

const TABLE_HEAD =
  "whitespace-nowrap px-4 py-3 font-neue text-[10px] font-medium uppercase tracking-[0.12em] text-white sm:text-xs";

const TABLE_CELL =
  "whitespace-nowrap px-4 py-3.5 font-neue text-sm text-white/90";

const TABLE_COLUMN_KEYS = [
  "Area",
  "Time",
  "CEST Offset",
  "Daily Loss Start",
  "Daily Loss End",
] as const;

export default function TimezoneConverterView() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const na = t("N/A");

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return TIMEZONE_AREAS;

    return TIMEZONE_AREAS.filter((row) =>
      row.area.toLowerCase().includes(query),
    );
  }, [searchQuery]);

  return (
    <div className="min-h-full w-full overflow-y-auto hide-scrollbar bg-[#050505] px-4 py-6 text-white sm:px-5">
      <div className="">
        <div className="relative max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("Search your timezone")}
            className="support-contact-field w-full !rounded-[8px] border border-white/10 bg-[#121212] py-2.5 pl-10 pr-10 font-neue text-sm text-white placeholder:text-white/40 focus:outline-none"
          />
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_448px]">
          <section className="overflow-hidden border border-white/10 bg-[#0b0b0b] rounded-xl">
            <div className="overflow-x-auto hide-scrollbar">
              <table className="w-full min-w-[720px] text-left ">
                <thead>
                  <tr className="bg-gradient-to-l from-[#DBD633]/30 via-[#9ED473]/30 to-[#1CCDE6]/30 rounded-t-xl">
                    {TABLE_COLUMN_KEYS.map((columnKey) => (
                      <th key={columnKey} className={TABLE_HEAD}>
                        {t(columnKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="rounded-b-xl">
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={TABLE_COLUMN_KEYS.length}
                        className="px-4 py-10 text-center font-neue text-sm text-white/45"
                      >
                        {t("No matching timezone found.")}
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-white/[0.06] transition-colors hover:bg-white/[0.02]"
                      >
                        <td className={`${TABLE_CELL} font-medium text-white`}>
                          {t(row.area)}
                        </td>
                        <td className={TABLE_CELL}>{na}</td>
                        <td className={TABLE_CELL}>{na}</td>
                        <td className={TABLE_CELL}>{na}</td>
                        <td className={TABLE_CELL}>{na}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="bg-gradient-to-r from-[#1CCDE6] via-[#9ED473] to-[#DBD633] p-[1px] rounded-2xl h-fit">
            <div className="rounded-2xl bg-[#0b0b0b]/90 p-5">
              <h2 className="font-neue text-lg font-medium text-white">
                {t("Timezones and Max Daily Loss")}
              </h2>
              <p className="mt-3 font-neue text-xs leading-relaxed text-white/80 sm:text-sm">
                {t(
                  "Please note that your current timezone differs from the one used to calculate our Max Daily Loss objective. This objective is based on Central European (Summer) Time — CE(S)T — which serves as the standard timezone for all FTMO traders. To help you understand when the Max Daily Loss resets in your local time, please refer to the conversion table below.",
                )}
              </p>

              <div className="mt-5 inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                <Clock className="h-4 w-4 shrink-0 text-white/50" />
                <span className="font-neue text-xs text-white/45 sm:text-sm">
                  {t("Today's permitted loss will reset in")}{" "}
                  <span className="font-medium text-white">{na}</span>
                </span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
